import { useCallback } from "react";
import { useI18n } from "./i18n-context";
import { getTranslation } from "./loadTranslations";
import { TranslationOptions, UseTranslationHook } from "./types/translations";
import { useBrand } from "@/app/shared/branding/brand-context";
import { applyBrandToTranslation } from "@/app/shared/branding/brand-text";

// Keep the original interface for backward compatibility
export type { UseTranslationHook };

// Function to replace placeholders in translation strings
// Supports both single {key} and double {{key}} curly brace formats
export const replacePlaceholders = (
	text: string,
	values?: Record<string, string | number>
): string => {
	if (!values) return text;

	return Object.entries(values).reduce((result, [key, value]) => {
		const stringValue = String(value);
		// Replace both {{key}} and {key} formats
		return result
			.replace(new RegExp(`{{${key}}}`, "g"), stringValue)
			.replace(new RegExp(`{${key}}`, "g"), stringValue);
	}, text);
};

// Original untyped factory function to create module-specific translation hooks
export function createModuleTranslationHook(moduleName: string) {
	return function useModuleTranslation(): UseTranslationHook {
		const { locale, setLocale, messages, isLoading } = useI18n();
		const { brand } = useBrand();
		const brandName = brand?.displayName ?? null;

		const t = useCallback(
			(key: string, options?: TranslationOptions): string => {
				// Handle nested keys and support both styles of access
				let fullKey: string;

				// Check if using nested access with dot notation
				if (key.includes(".") && !key.startsWith(`${moduleName}.`)) {
					// First check for direct access with the module prefix
					fullKey = `${moduleName}.${key}`;
				} else if (!key.includes(".")) {
					// Simple key without dots - add module prefix
					fullKey = `${moduleName}.${key}`;
				} else {
					// Key already contains module prefix or is a full path
					fullKey = key;
				}

				const translation = getTranslation(
					messages,
					fullKey,
					options?.fallback
				);
				// Rebrand before interpolation, so a value supplied at the call site
				// (a company or supplier name) is never rewritten.
				return replacePlaceholders(
					applyBrandToTranslation(fullKey, translation, brandName),
					options?.values
				);
			},
			[messages, brandName]
		);

		return {
			t,
			locale,
			setLocale,
			isLoading,
			messages,
		};
	};
}
